import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, X, Loader2, FileImage, FileVideo, FileText, Music, Check, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { resourcesAPI, scriptAudioAPI } from '../../services/api';
import { conversationUpdatesService, type TranscriptionEvent } from '../../services/conversationUpdatesService';

// Accepted file types
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
const ACCEPTED_AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/flac', 'audio/aac', 'audio/webm', 'audio/x-m4a'];
const ACCEPTED_SCRIPT_TYPES = ['text/plain', 'text/vtt', 'application/x-subrip', 'text/markdown'];
const ACCEPTED_TYPES = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES, ...ACCEPTED_AUDIO_TYPES, ...ACCEPTED_SCRIPT_TYPES];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

// Script file extensions (for accept attribute)
const SCRIPT_EXTENSIONS = '.txt,.srt,.vtt,.md';
const AUDIO_EXTENSIONS = '.mp3,.wav,.ogg,.m4a,.flac,.aac,.webm';

interface UploadedAsset {
  src: { original: string; large: string };
  photographer: string;
  source: string;
  isUpload: true;
  gcsPath: string;
  originalName: string;
  mimeType: string;
  resourceId: string;
}

interface UploadPanelProps {
  conversationId: string;
  onSelect: (asset: UploadedAsset) => void;
  onClose: () => void;
}

interface UploadState {
  file: File | null;
  preview: string | null;
  uploading: boolean;
  uploaded: boolean;
  error: string | null;
  progress: number;
  result: {
    signedUrl: string;
    baseUrl?: string;
    gcsPath: string;
    resourceId: string;
    mimeType: string;
  } | null;
  // Audio transcription state
  transcribing: boolean;
  transcription: {
    transcript: string;
    wordCount: number;
    duration: number;
  } | null;
}

export const UploadPanel: React.FC<UploadPanelProps> = ({
  conversationId,
  onSelect,
  onClose,
}) => {
  const [state, setState] = useState<UploadState>({
    file: null,
    preview: null,
    uploading: false,
    uploaded: false,
    error: null,
    progress: 0,
    result: null,
    transcribing: false,
    transcription: null,
  });

  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Listen for transcription events from worker via SSE
  useEffect(() => {
    const unsubscribe = conversationUpdatesService.onTranscriptionEvent(
      (eventConversationId: string, event: TranscriptionEvent) => {
        if (eventConversationId !== conversationId) return;

        if (event.type === 'transcription_complete') {
          setState(prev => ({
            ...prev,
            transcribing: false,
            transcription: {
              transcript: event.transcript || '',
              wordCount: event.wordCount || 0,
              duration: event.duration || 0,
            },
          }));
        } else if (event.type === 'transcription_error') {
          setState(prev => ({
            ...prev,
            transcribing: false,
            error: `Uploaded successfully, but transcription failed: ${event.error || 'Unknown error'}. You can continue without transcription.`,
          }));
        }
      }
    );

    return unsubscribe;
  }, [conversationId]);

  // Validate file
  const validateFile = (file: File): string | null => {
    // Also allow by extension for script files that browsers may not assign correct MIME
    const ext = file.name.toLowerCase().split('.').pop();
    const scriptExtensions = ['txt', 'srt', 'vtt', 'md'];
    const isScriptByExt = scriptExtensions.includes(ext || '');

    if (!ACCEPTED_TYPES.includes(file.type) && !isScriptByExt) {
      return `Unsupported file type: ${file.type}. Accepted: images, videos, audio (mp3, wav, ogg), scripts (txt, srt, vtt)`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB. Maximum: 100MB`;
    }
    return null;
  };

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    const error = validateFile(file);
    if (error) {
      setState(prev => ({ ...prev, error, file: null, preview: null }));
      return;
    }

    // Create preview URL
    const preview = URL.createObjectURL(file);
    setState({
      file,
      preview,
      uploading: false,
      uploaded: false,
      error: null,
      progress: 0,
      result: null,
      transcribing: false,
      transcription: null,
    });
  }, []);

  // Handle drag events
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Upload file
  const handleUpload = useCallback(async () => {
    if (!state.file || !conversationId) return;

    setState(prev => ({ ...prev, uploading: true, error: null, progress: 0 }));

    try {
      const result = await resourcesAPI.uploadMedia(
        conversationId,
        state.file,
        (progress) => setState(prev => ({ ...prev, progress }))
      );

      setState(prev => ({
        ...prev,
        uploading: false,
        uploaded: true,
        progress: 100,
        result: {
          signedUrl: result.signedUrl,
          baseUrl: result.baseUrl,
          gcsPath: result.gcsPath,
          resourceId: result.resourceId,
          mimeType: result.mimeType,
        },
      }));

      // Auto-trigger transcription for audio files (async via worker queue + SSE)
      const isAudio = state.file.type.startsWith('audio/');
      if (isAudio && result.resourceId) {
        setState(prev => ({ ...prev, transcribing: true }));
        try {
          // Enqueues job on worker — results come via SSE transcription events
          await scriptAudioAPI.transcribeAudio(conversationId, result.resourceId);
        } catch (transcribeError) {
          console.error('Failed to queue transcription:', transcribeError);
          setState(prev => ({
            ...prev,
            transcribing: false,
            error: `Uploaded successfully, but failed to start transcription: ${transcribeError instanceof Error ? transcribeError.message : 'Unknown error'}. You can continue without transcription.`,
          }));
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setState(prev => ({
        ...prev,
        uploading: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      }));
    }
  }, [state.file, conversationId]);

  // Handle confirmation
  const handleConfirm = useCallback(() => {
    if (!state.result || !state.file) return;

    // Prefer baseUrl (plain HTTPS path) for src.original so stored URLs never expire.
    // The serve layer re-signs on demand. Fall back to signedUrl for backwards compatibility.
    const assetUrl = state.result.baseUrl || state.result.signedUrl;
    onSelect({
      src: { original: assetUrl, large: state.result.signedUrl },
      photographer: 'Uploaded by user',
      source: 'upload',
      isUpload: true,
      gcsPath: state.result.gcsPath,
      originalName: state.file.name,
      mimeType: state.result.mimeType,
      resourceId: state.result.resourceId,
    });
  }, [state.result, state.file, onSelect]);

  // Reset state
  const handleReset = useCallback(() => {
    if (state.preview) {
      URL.revokeObjectURL(state.preview);
    }
    setState({
      file: null,
      preview: null,
      uploading: false,
      uploaded: false,
      error: null,
      progress: 0,
      result: null,
      transcribing: false,
      transcription: null,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [state.preview]);

  const isVideo = state.file?.type.startsWith('video/');
  const isAudio = state.file?.type.startsWith('audio/');
  const isScript = state.file ? (
    state.file.type.startsWith('text/') ||
    state.file.type === 'application/x-subrip' ||
    ['txt', 'srt', 'vtt', 'md'].includes(state.file.name.split('.').pop()?.toLowerCase() || '')
  ) : false;

  return (
    <div className="flex flex-col h-full">
      {/* Main content area */}
      <div className="flex-1 overflow-y-auto p-4">
        {!state.file ? (
          // Drop zone
          <div
            className={cn(
              "h-full min-h-[300px] border-2 border-dashed rounded-lg transition-all duration-200 flex flex-col items-center justify-center cursor-pointer",
              isDragOver
                ? "border-primary bg-primary/10"
                : "border-border hover:border-primary/50 hover:bg-muted/30"
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center gap-4 p-8 text-center">
              <div className={cn(
                "w-16 h-16 rounded-full flex items-center justify-center transition-colors",
                isDragOver ? "bg-primary/20" : "bg-muted"
              )}>
                <Upload className={cn(
                  "w-8 h-8",
                  isDragOver ? "text-primary" : "text-muted-foreground"
                )} />
              </div>
              <div>
                <p className="text-lg font-medium text-foreground">
                  {isDragOver ? "Drop your file here" : "Drag & drop or click to upload"}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Images, Videos, Audio (MP3, WAV), or Scripts (TXT, SRT, VTT)
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum file size: 100MB
                </p>
              </div>
              <Button variant="outline" size="sm" className="mt-2">
                Browse Files
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={[...ACCEPTED_TYPES, SCRIPT_EXTENSIONS, AUDIO_EXTENSIONS].join(',')}
              onChange={handleInputChange}
              className="hidden"
            />
          </div>
        ) : (
          // Preview area
          <div className="h-full flex flex-col">
            {/* Preview */}
            <div className="flex-1 relative bg-muted/30 rounded-lg overflow-hidden flex items-center justify-center min-h-[300px]">
              {isAudio ? (
                <div className="flex flex-col items-center gap-4 p-8">
                  <Music className="w-16 h-16 text-muted-foreground" />
                  <p className="text-sm font-medium">{state.file.name}</p>
                  <audio src={state.preview!} controls className="w-full max-w-sm" />
                  <p className="text-xs text-muted-foreground">
                    {(state.file.size / 1024 / 1024).toFixed(1)} MB
                  </p>
                  {state.transcribing && (
                    <div className="flex items-center gap-2 text-sm text-primary">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Transcribing your audio...
                    </div>
                  )}
                  {state.transcription && (
                    <div className="w-full max-w-sm bg-background rounded-md p-3 border text-xs">
                      <div className="flex justify-between text-muted-foreground mb-1">
                        <span>{state.transcription.wordCount} words</span>
                        <span>{Math.floor(state.transcription.duration / 60)}:{String(Math.floor(state.transcription.duration % 60)).padStart(2, '0')}</span>
                      </div>
                      <p className="line-clamp-4 text-foreground">{state.transcription.transcript}</p>
                    </div>
                  )}
                </div>
              ) : isScript ? (
                <div className="flex flex-col items-center gap-4 p-8">
                  <FileText className="w-16 h-16 text-muted-foreground" />
                  <p className="text-sm font-medium">{state.file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(state.file.size / 1024).toFixed(0)} KB - Script file
                  </p>
                </div>
              ) : isVideo ? (
                <video
                  src={state.preview!}
                  className="max-w-full max-h-full object-contain"
                  controls
                  autoPlay
                  muted
                  loop
                />
              ) : (
                <img
                  src={state.preview!}
                  alt={state.file.name}
                  className="max-w-full max-h-full object-contain"
                />
              )}

              {/* Remove button */}
              {!state.uploading && (
                <button
                  onClick={handleReset}
                  className="absolute top-2 right-2 w-8 h-8 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              )}

              {/* Upload progress overlay */}
              {state.uploading && (
                <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                  <Loader2 className="w-10 h-10 text-white animate-spin mb-4" />
                  <div className="w-48 h-2 bg-white/20 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${state.progress}%` }}
                    />
                  </div>
                  <p className="text-white text-sm mt-2">{state.progress}% uploaded</p>
                </div>
              )}

              {/* Success overlay */}
              {state.uploaded && (
                <div className="absolute top-2 left-2 px-3 py-1.5 bg-green-600 text-white rounded-full flex items-center gap-1.5 text-sm">
                  <Check className="w-4 h-4" />
                  <span>Uploaded</span>
                </div>
              )}
            </div>

            {/* File info */}
            <div className="mt-4 p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  {isVideo ? (
                    <FileVideo className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <FileImage className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{state.file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(state.file.size / 1024 / 1024).toFixed(2)} MB
                    {state.file.type && ` • ${state.file.type.split('/')[1].toUpperCase()}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Error message */}
            {state.error && (
              <div className="mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{state.error}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer with actions */}
      <div className="flex-shrink-0 border-t border-border/50 px-4 py-3 flex items-center justify-between bg-muted/10">
        <div className="text-xs text-muted-foreground">
          {state.file && !state.uploaded && !state.uploading && (
            <span>Ready to upload</span>
          )}
          {state.uploading && (
            <span>Uploading...</span>
          )}
          {state.uploaded && (
            <span className="text-green-600">Upload complete</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onClose} className="h-8">
            Cancel
          </Button>
          {state.file && !state.uploaded && (
            <Button
              onClick={handleUpload}
              disabled={state.uploading}
              size="sm"
              className="h-8"
            >
              {state.uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          )}
          {state.uploaded && (
            <Button onClick={handleConfirm} size="sm" className="h-8">
              Use This
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
